import React, { FC, useEffect, useRef, useState } from "react"
import { KSpan } from "kahuna-base-react-components"
import { addCommasToNumber } from "../../../../../utility"

const FONT = "Inter var, Inter, system-ui, -apple-system, sans-serif"
const TEXT_MUTED = "#6b7280"
const TEXT_PRIMARY = "#111"
const EXPECTED_COLOR = "#94a3b8"
const ACTUAL_COLOR = "#1f2937"

type GapState = "behind" | "ahead" | "within"
const GAP_STYLE: Record<GapState, { fill: string; dot: string; label: string }> = {
  behind: { fill: "rgba(239, 68, 68, 0.15)", dot: "#ef4444", label: "Behind" },
  within: { fill: "rgba(148, 163, 184, 0.15)", dot: "#94a3b8", label: "Within Range" },
  ahead: { fill: "rgba(34, 197, 94, 0.15)", dot: "#22c55e", label: "Ahead" }
}
// Beyond one month of expected collections (the tolerance) either way is behind / ahead
const GAP_BORDER = "#e5e7eb"
const gapState = (gap: number, tolerance: number): GapState =>
  gap < -tolerance ? "behind" : gap > tolerance ? "ahead" : "within"

// Size before the first measurement
const DEFAULT_W = 1000
const DEFAULT_H = 300
const PAD_L = 56
const PAD_R = 12
const PAD_T = 14
const PAD_B = 26

const usd = (value: number) => `$${addCommasToNumber(Math.round(value))}`
const signedUsd = (value: number) => `${value < 0 ? "-" : value > 0 ? "+" : ""}${usd(Math.abs(value))}`
const axisUsd = (value: number) =>
  value === 0
    ? "$0"
    : value >= 1_000_000
    ? `$${+(value / 1_000_000).toFixed(1)}M`
    : value >= 1_000
    ? `$${+(value / 1_000).toFixed(1)}K`
    : `$${value}`

const niceStep = (range: number) => {
  const rough = range / 5
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)))
  const fraction = rough / magnitude
  return (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10) * magnitude
}

type Pt = [number, number]
const lerp = (a: Pt, b: Pt, t: number): Pt => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]

type TargetActualPlotProps = {
  // Short axis labels and full tooltip labels, one per month
  labels: string[]
  fullLabels: string[]
  expected: number[]
  // null once the data stops (months not reported yet)
  actual: (number | null)[]
  tolerance: number
}

const TargetActualPlot: FC<TargetActualPlotProps> = ({ labels, fullLabels, expected, actual, tolerance }) => {
  const wrapRef = useRef<HTMLDivElement>(null)
  // The plot fills the space it is given, drawn at the real pixel size so text and dots never stretch
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H })
  const W = size.w
  const H = size.h
  const PLOT_W = W - PAD_L - PAD_R
  const PLOT_H = H - PAD_T - PAD_B
  useEffect(() => {
    const node = wrapRef.current
    if (!node) return
    const measure = () => {
      const { width, height } = node.getBoundingClientRect()
      if (width > 0 && height > 0) setSize({ w: Math.round(width), h: Math.round(height) })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  const [tip, setTip] = useState<{ index: number } | null>(null)

  const n = labels.length
  const top = Math.max(1, ...expected, ...actual.map((v) => v ?? 0))
  const step = niceStep(top)
  const yMax = Math.ceil(top / step) * step
  const ticks: number[] = []
  for (let v = 0; v <= yMax + step / 2; v += step) ticks.push(v)

  const x = (i: number) => PAD_L + (n > 1 ? (i / (n - 1)) * PLOT_W : 0)
  const y = (v: number) => PAD_T + PLOT_H - (v / yMax) * PLOT_H
  const expPts: Pt[] = expected.map((v, i) => [x(i), y(v)])
  const actPts: (Pt | null)[] = actual.map((v, i) => (v === null ? null : [x(i), y(v)]))
  const path = (pts: Pt[]) => pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ")

  // The area between the two lines, one polygon per stretch of one colour. A stretch is cut where
  // the lines cross, so red and green meet exactly at the crossing instead of at a month.
  const areas: { d: string; state: GapState }[] = []
  for (let i = 0; i < n - 1; i++) {
    const a0 = actual[i]
    const a1 = actual[i + 1]
    if (a0 === null || a1 === null) continue
    const g0 = a0 - expected[i]
    const g1 = a1 - expected[i + 1]
    // Cut where the gap crosses either edge of the within-range band, so colours change exactly there
    const cuts = [0, 1]
    ;[-tolerance, tolerance].forEach((edge) => {
      if ((g0 - edge) * (g1 - edge) < 0) cuts.push((edge - g0) / (g1 - g0))
    })
    cuts.sort((a, b) => a - b)
    for (let k = 0; k < cuts.length - 1; k++) {
      const t0 = cuts[k]
      const t1 = cuts[k + 1]
      if (t1 - t0 < 1e-6) continue
      const state = gapState(g0 + (g1 - g0) * ((t0 + t1) / 2), tolerance)
      const p0 = lerp(actPts[i] as Pt, actPts[i + 1] as Pt, t0)
      const p1 = lerp(actPts[i] as Pt, actPts[i + 1] as Pt, t1)
      const e0 = lerp(expPts[i], expPts[i + 1], t0)
      const e1 = lerp(expPts[i], expPts[i + 1], t1)
      areas.push({ d: `M${p0} L${p1} L${e1} L${e0} Z`, state })
    }
  }

  const labelEvery = n > 30 ? 3 : n > 18 ? 2 : 1
  const tipPoint = tip ? (actPts[tip.index] as Pt) : null
  const tipLeft = tipPoint ? tipPoint[0] : 0
  const flip = tipPoint ? tipLeft > W - 240 : false
  const tipGap = tip ? (actual[tip.index] as number) - expected[tip.index] : 0
  const tipState = gapState(tipGap, tolerance)

  const legendText = { fontSize: 11, fontWeight: 600, color: TEXT_MUTED, letterSpacing: "0.03em", lineHeight: "16px" }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-center flex-wrap gap-x-6 gap-y-2 mb-2">
        <div className="flex items-center gap-1.5">
          <span style={{ width: 20, borderTop: `2px dashed ${EXPECTED_COLOR}`, display: "inline-block" }} />
          <KSpan text="EXPECTED" {...legendText} />
        </div>
        <div className="flex items-center gap-1.5">
          <span style={{ width: 20, borderTop: `2px solid ${ACTUAL_COLOR}`, display: "inline-block" }} />
          <KSpan text="ACTUAL COLLECTED" {...legendText} />
        </div>
        <span style={{ width: 1, height: 14, background: GAP_BORDER }} />
        {(["behind", "within", "ahead"] as GapState[]).map((state) => (
          <div key={state} className="flex items-center gap-1.5">
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: GAP_STYLE[state].dot,
                display: "inline-block"
              }}
            />
            <KSpan text={GAP_STYLE[state].label.toUpperCase()} {...legendText} />
          </div>
        ))}
      </div>

      <div
        ref={wrapRef}
        className="relative flex-1"
        style={{ fontFamily: FONT, minHeight: 260 }}
        onMouseLeave={() => setTip(null)}
      >
        {/* Absolutely placed so its own size never feeds back into the space it is measured from */}
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width={W}
          height={H}
          style={{ position: "absolute", inset: 0, display: "block" }}
        >
          {ticks.map((v) => (
            <g key={v}>
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={Math.round(y(v)) + 0.5}
                y2={Math.round(y(v)) + 0.5}
                stroke="#f1f5f9"
              />
              <text x={PAD_L - 8} y={y(v) + 4} textAnchor="end" fontSize={11} fill="#666">
                {axisUsd(v)}
              </text>
            </g>
          ))}
          <text
            x={12}
            y={PAD_T + PLOT_H / 2}
            textAnchor="middle"
            fontSize={11}
            fill="#666"
            transform={`rotate(-90 12 ${PAD_T + PLOT_H / 2})`}
          >
            Cumulative Collections (USD)
          </text>
          {labels.map(
            (label, i) =>
              (i % labelEvery === 0 || i === n - 1) && (
                <text
                  key={i}
                  x={x(i)}
                  y={H - 8}
                  textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
                  fontSize={11}
                  fill="#666"
                >
                  {label}
                </text>
              )
          )}

          {areas.map((area, i) => (
            <path key={i} d={area.d} fill={GAP_STYLE[area.state].fill} stroke="none" />
          ))}

          <path d={path(expPts)} fill="none" stroke={EXPECTED_COLOR} strokeWidth={2} strokeDasharray="5 4" />
          <path
            d={path(actPts.filter((p): p is Pt => p !== null))}
            fill="none"
            stroke={ACTUAL_COLOR}
            strokeWidth={2.5}
            strokeLinejoin="round"
          />
          <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={PAD_T + PLOT_H} stroke="rgba(0,0,0,0.1)" />
          <line x1={PAD_L} x2={W - PAD_R} y1={PAD_T + PLOT_H} y2={PAD_T + PLOT_H} stroke="rgba(0,0,0,0.1)" />

          {actPts.map(
            (p, i) =>
              p && (
                <g key={i} onMouseEnter={() => setTip({ index: i })}>
                  <circle cx={p[0]} cy={p[1]} r={3.25} fill={ACTUAL_COLOR} />
                  <circle cx={p[0]} cy={p[1]} r={9} fill="transparent" />
                </g>
              )
          )}
        </svg>

        {tip && tipPoint && (
          <div
            style={{
              position: "absolute",
              zIndex: 30,
              left: flip ? undefined : tipLeft + 14,
              right: flip ? W - tipLeft + 14 : undefined,
              top: Math.max(tipPoint[1] - 20, 0),
              minWidth: 190,
              padding: "10px 12px",
              background: "#fff",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              fontSize: 12,
              lineHeight: 1.4,
              color: TEXT_PRIMARY,
              pointerEvents: "none"
            }}
          >
            <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>{fullLabels[tip.index]}</div>
            {[
              { label: "Expected", value: usd(expected[tip.index]) },
              { label: "Actual Collected", value: usd(actual[tip.index] as number) },
              { label: "Variance", value: signedUsd(tipGap) },
              { label: "Performance", value: GAP_STYLE[tipState].label }
            ].map((row) => (
              <div
                key={row.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 16,
                  padding: "2px 0"
                }}
              >
                <span style={{ color: TEXT_MUTED }}>{row.label}</span>
                <span style={{ fontWeight: 600, textAlign: "right" }}>{row.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default TargetActualPlot
